import Cookies from 'js-cookie';

/**
 * Gets a cookie value from the browser
 * @param cookieName The name of the cookie to retrieve
 * @returns The cookie value or undefined if not found
 */
export function getCookie(cookieName: string): string | undefined {
	return Cookies.get(cookieName);
}

/**
 * Sets a cookie in the browser
 * @param cookieName The name of the cookie
 * @param value The value to set
 * @param options Cookie options (expires, path, domain, secure, sameSite)
 */
export function setCookie(
	cookieName: string,
	value: string | object,
	options?: Cookies.CookieAttributes
): void {
	// If value is an object, stringify it
	const cookieValue = typeof value === 'object' ? JSON.stringify(value) : value;
	Cookies.set(cookieName, cookieValue, options);
}

/**
 * Updates a cookie value by merging with existing value
 */
export function updateCookie(
	cookieName: string,
	updates: Record<string, any>,
	options?: Cookies.CookieAttributes
): void {
	const currentValue = getCookie(cookieName);
	let currentData: Record<string, any> = {};

	// Try to parse existing cookie as JSON
	if (currentValue) {
		try {
			const parsed = JSON.parse(currentValue);
			if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
				currentData = parsed;
			}
		} catch {
			// If not JSON, treat as empty object
		}
	}

	// Merge updates with current data
	const updatedData = { ...currentData, ...updates };

	// Set the updated cookie
	setCookie(cookieName, updatedData, options);
}

/**
 * Removes a cookie
 */
export function removeCookie(cookieName: string, options?: Cookies.CookieAttributes): void {
	Cookies.remove(cookieName, options);
}

/**
 * Gets all cookies as an object
 * @returns Object containing all cookies
 */
export function getAllCookies(): { [key: string]: string } {
	return Cookies.get();
}

/**
 * Serializes to JSON string for cookie storage
 */
export function serializeCookieValue(flags: Record<string, boolean>): string {
	return JSON.stringify(flags);
}
